import { useState, useEffect, useCallback, useRef } from 'react';

export interface ProductCardHandle {
  cycleFormat: () => void;
  incrementQuantity: () => void;
  decrementQuantity: () => void;
  addToCart: () => void;
}

interface UseGridKeyboardNavOptions {
  enabled: boolean;
  itemCount: number;
  gridRef: React.RefObject<HTMLDivElement | null>;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  cardHandlesRef: React.MutableRefObject<Map<number, ProductCardHandle>>;
  isSearchFocused: boolean;
  isInnerInputFocused: boolean;
  sentinelRef?: React.RefObject<HTMLDivElement | null>;
  hasMore?: boolean;
}

interface UseGridKeyboardNavReturn {
  focusedIndex: number | null;
  setFocusedIndex: (index: number | null) => void;
  isHelpOpen: boolean;
  setIsHelpOpen: (open: boolean) => void;
}

function getColumnCount(gridEl: HTMLDivElement): number {
  const cols = getComputedStyle(gridEl).gridTemplateColumns;
  // cols is like "200px 200px 200px" — count the segments
  return cols.split(/\s+/).filter(Boolean).length || 1;
}

export function useGridKeyboardNav({
  enabled,
  itemCount,
  gridRef,
  searchInputRef,
  cardHandlesRef,
  isSearchFocused,
  isInnerInputFocused,
  sentinelRef,
  hasMore,
}: UseGridKeyboardNavOptions): UseGridKeyboardNavReturn {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const prevItemCountRef = useRef(itemCount);

  // Reset focusedIndex when item count changes (category switch, search)
  useEffect(() => {
    if (itemCount !== prevItemCountRef.current) {
      setFocusedIndex(null);
      prevItemCountRef.current = itemCount;
    }
  }, [itemCount]);

  // Scroll focused card into view
  useEffect(() => {
    if (focusedIndex === null || !gridRef.current) return;
    const card = gridRef.current.children[focusedIndex] as HTMLElement | undefined;
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [focusedIndex, gridRef]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // Don't interfere when help modal is open (except Escape to close it)
    if (isHelpOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsHelpOpen(false);
      }
      return;
    }

    const target = e.target as HTMLElement;
    const tagName = target?.tagName;

    // When quantity input is focused, let it handle everything
    if (isInnerInputFocused) {
      return;
    }

    // When search is focused: ArrowDown exits search and selects first product
    if (isSearchFocused) {
      if (e.key === 'ArrowDown' && itemCount > 0) {
        e.preventDefault();
        (target as HTMLInputElement).blur();
        setFocusedIndex(0);
      } else if (e.key === 'Escape' && tagName === 'INPUT') {
        e.preventDefault();
        (target as HTMLInputElement).blur();
      }
      return;
    }

    // Skip when typing in other inputs
    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
      if (e.key === 'Escape') {
        (target as HTMLInputElement).blur();
        e.preventDefault();
        return;
      }
      return;
    }

    // Skip when modifier keys are held (allow Ctrl+K for search)
    if (e.altKey) return;
    if ((e.ctrlKey || e.metaKey) && e.key !== 'k') return;

    const grid = gridRef.current;

    switch (e.key) {
      case '?': {
        e.preventDefault();
        setIsHelpOpen(true);
        break;
      }

      case '/': {
        // SearchBar already handles this, but if it doesn't:
        e.preventDefault();
        searchInputRef.current?.focus();
        setFocusedIndex(null);
        break;
      }

      case 'Escape': {
        e.preventDefault();
        setFocusedIndex(null);
        break;
      }

      case 'ArrowDown': {
        e.preventDefault();
        if (itemCount === 0) break;
        if (focusedIndex === null) {
          setFocusedIndex(0);
        } else if (grid) {
          const cols = getColumnCount(grid);
          const next = focusedIndex + cols;
          if (next < itemCount) {
            setFocusedIndex(next);
          } else if (hasMore && sentinelRef?.current) {
            sentinelRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        }
        break;
      }

      case 'ArrowUp': {
        e.preventDefault();
        if (itemCount === 0) break;
        if (focusedIndex === null) {
          setFocusedIndex(0);
        } else if (grid) {
          const cols = getColumnCount(grid);
          const next = focusedIndex - cols;
          if (next >= 0) setFocusedIndex(next);
        }
        break;
      }

      case 'ArrowRight': {
        e.preventDefault();
        if (itemCount === 0) break;
        if (focusedIndex === null) {
          setFocusedIndex(0);
        } else {
          const next = focusedIndex + 1;
          if (next < itemCount) {
            setFocusedIndex(next);
          } else if (hasMore && sentinelRef?.current) {
            sentinelRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        }
        break;
      }

      case 'ArrowLeft': {
        e.preventDefault();
        if (itemCount === 0) break;
        if (focusedIndex === null) {
          setFocusedIndex(0);
        } else {
          const next = focusedIndex - 1;
          if (next >= 0) setFocusedIndex(next);
        }
        break;
      }

      case 'f': {
        if (focusedIndex !== null) {
          e.preventDefault();
          cardHandlesRef.current.get(focusedIndex)?.cycleFormat();
        }
        break;
      }

      case '+':
      case '=': {
        if (focusedIndex !== null) {
          e.preventDefault();
          cardHandlesRef.current.get(focusedIndex)?.incrementQuantity();
        }
        break;
      }

      case '-': {
        if (focusedIndex !== null) {
          e.preventDefault();
          cardHandlesRef.current.get(focusedIndex)?.decrementQuantity();
        }
        break;
      }

      case 'Enter': {
        if (focusedIndex !== null) {
          e.preventDefault();
          cardHandlesRef.current.get(focusedIndex)?.addToCart();
        }
        break;
      }
    }
  }, [
    enabled, isHelpOpen, isSearchFocused, isInnerInputFocused,
    focusedIndex, itemCount, gridRef, searchInputRef, cardHandlesRef, sentinelRef, hasMore,
  ]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { focusedIndex, setFocusedIndex, isHelpOpen, setIsHelpOpen };
}
